import type { MaybeRefOrGetter } from 'vue'

export type BeaconLink = {
  title: string
  source: string
  category: string
  website_only: boolean
  url: string
}

const beaconCache = new Map<string, BeaconLink[]>()
const runningFetchResults = new Map<string, { isFetching: Ref<boolean>; links: Ref<any> }>()

function useAllBeacons(gnd: MaybeRefOrGetter<string | undefined>) {
  const hasGnd = computed(() => typeof toValue(gnd) === 'string')

  const endpoint = computed(() => {
    if (!hasGnd.value) return ''

    // Use dev page root for development
    const base = import.meta.env.DEV ? 'https://sh.personenportal.ch' : ''

    return `${base}/wp-json/person-filter/beacon/find/${toValue(gnd)}`
  })

  // Reference to the results of other, parallel fetches
  const referenceToOtherFetchLoadingState = ref<Ref<boolean>>()
  const referenceToOtherFetchResults = ref<Ref<any>>()

  const {
    data: links,
    isFetching,
    error,
  } = useFetch(endpoint, {
    immediate: hasGnd.value,
    refetch: hasGnd,
    initialData: [],
    beforeFetch({ url, cancel }) {
      referenceToOtherFetchLoadingState.value = undefined
      referenceToOtherFetchResults.value = undefined

      // If there's a cached result, use it
      if (beaconCache.has(url)) {
        links.value = beaconCache.get(url)!
        cancel()
        return
      }

      // If there's a running fetch for this URL, use its result
      if (runningFetchResults.has(url)) {
        referenceToOtherFetchLoadingState.value = runningFetchResults.get(url)!.isFetching
        referenceToOtherFetchResults.value = runningFetchResults.get(url)!.links
        cancel()
        return
      }

      runningFetchResults.set(url, { links, isFetching })
    },
    onFetchError(ctx) {
      runningFetchResults.delete(ctx.response?.url ?? '')
      return ctx
    },
    afterFetch(ctx) {
      runningFetchResults.delete(ctx.response.url)
      beaconCache.set(ctx.response.url, ctx.data as BeaconLink[])
      return ctx
    },
  }).json<BeaconLink[]>()

  return {
    isFetching: computed<boolean>(
      () => referenceToOtherFetchLoadingState.value?.value ?? isFetching.value,
    ),
    error,
    links: computed<BeaconLink[]>(() => referenceToOtherFetchResults.value?.value ?? links.value),
  }
}

export function useBeacons(
  gnd: MaybeRefOrGetter<string | undefined>,
  category?: MaybeRefOrGetter<string | undefined>,
) {
  const { links, isFetching, error } = useAllBeacons(gnd)

  const filteredLinks = computed(() =>
    toValue(category) == null
      ? links.value
      : links.value.filter(link => link.category === toValue(category)),
  )

  return {
    isFetching,
    error,
    links: filteredLinks,
  }
}
